import React, { useState } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSpinner, 
  faLock, 
  faEye, 
  faEyeSlash,
  faKey,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import "./index.css";

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<boolean>;
}

interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  show,
  onHide,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<FormData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters long";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const success = await onSubmit(formData);
      if (success) {
        // Reset form on success
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setErrors({});
      }
    } catch (error) {
      console.error("Password change error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
      setShowPasswords({ current: false, new: false, confirm: false });
      onHide();
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: "", color: "" };
    if (password.length < 6) return { strength: 1, label: "Too Short", color: "danger" };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score < 2) return { strength: 2, label: "Weak", color: "danger" };
    if (score < 4) return { strength: 3, label: "Fair", color: "warning" };
    if (score < 5) return { strength: 4, label: "Good", color: "info" };
    return { strength: 5, label: "Strong", color: "success" };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
          Change Password
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="text-center mb-4">
          <p className="text-muted">
            Please enter your current password and choose a new secure password.
          </p>
        </div>
        
        <Form onSubmit={handleSubmit}>
          {/* Current Password */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Current Password
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.current ? "text" : "password"}
                placeholder="Enter your current password"
                value={formData.currentPassword}
                onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                isInvalid={!!errors.currentPassword}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility("current")}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
            {errors.currentPassword && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.currentPassword}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* New Password */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FontAwesomeIcon icon={faKey} className="me-2" />
              New Password
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.new ? "text" : "password"}
                placeholder="Enter your new password"
                value={formData.newPassword}
                onChange={(e) => handleInputChange("newPassword", e.target.value)}
                isInvalid={!!errors.newPassword}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility("new")}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
            
            {/* Password Strength Indicator */}
            {formData.newPassword && (
              <div className="mt-2">
                <div className="d-flex justify-content-between align-items-center">
                  <small>Password Strength:</small>
                  <small className={`text-${passwordStrength.color} fw-bold`}>
                    {passwordStrength.label}
                  </small>
                </div>
                <div className="progress mt-1" style={{ height: "4px" }}>
                  <div
                    className={`progress-bar bg-${passwordStrength.color}`}
                    style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                  />
                </div>
              </div>
            )}
            
            {errors.newPassword && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.newPassword}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Confirm New Password */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FontAwesomeIcon icon={faKey} className="me-2" />
              Confirm New Password
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.confirm ? "text" : "password"}
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                isInvalid={!!errors.confirmPassword}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility("confirm")}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
            {errors.confirmPassword && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Security Tips */}
          <Alert variant="info" className="small">
            <strong>Security Tips:</strong>
            <ul className="mb-0 mt-1">
              <li>Use at least 8 characters</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Add numbers and special characters</li>
              <li>Avoid common words or personal information</li>
            </ul>
          </Alert>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
          style={{ backgroundColor: "#FF1001", borderColor: "#FF1001" }}
        >
          {isLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Changing Password...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Change Password
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePasswordModal;